import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useLazyQuery } from '@parkchirp/api-client';
import React, { FC, useCallback, useState } from 'react';
import { GET_LEASE_MANAGER_ALLOCATION_REPORT } from '../../queries';
import type { SetShowSnackbar } from '../../types/alerts';
import { Dayjs, dayjs } from '../../utils';

interface LeaseManagerAllocationReportProps {
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
}

export const LeaseManagerAllocationReportForm: FC<LeaseManagerAllocationReportProps> = ({
	facilityId,
	setShowSnackbar,
}) => {
	const [asOfDate, setAsOfDate] = useState<Dayjs>(dayjs());
	const [isDownloadingXlsx, setIsDownloadingXlsx] = useState<boolean>(false);

	const [getLeaseManagerAllocationReport] = useLazyQuery(GET_LEASE_MANAGER_ALLOCATION_REPORT, {
		fetchPolicy: 'network-only',
	});

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: 'Please select a facility.',
				failingCondition: !facilityId,
			},
			{
				message: 'Please select an As of Date.',
				failingCondition: !asOfDate,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [asOfDate, facilityId, setShowSnackbar]);

	const handleReset = () => setAsOfDate(null);

	const handleDownload = async () => {
		const validForm = handleValidation();

		if (!validForm) return;

		setIsDownloadingXlsx(true);

		const { error } = await getLeaseManagerAllocationReport({
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			variables: {
				facilityId,
				asOfDate: asOfDate.format('YYYY-MM-DD'),
			},
			onCompleted: (data: {
				getLeaseManagerAllocationReport: {
					url: string;
				};
			}) => {
				const fileLink = document.createElement('a');
				fileLink.href = data.getLeaseManagerAllocationReport.url;
				fileLink.click();
			},
		});
		if (error) {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
		}

		setIsDownloadingXlsx(false);
	};

	return (
		<Box sx={{ paddingTop: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker<Dayjs>
							value={asOfDate}
							label='As of Date*'
							slotProps={{
								textField: {
									fullWidth: true,
									required: true,
									inputProps: { 'data-cy': 'as-of-date-picker' },
								},
							}}
							onChange={setAsOfDate}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', mt: 3 }}>
				<Grid item>
					<Button variant='contained' size='small' onClick={handleDownload} disabled={isDownloadingXlsx}>
						{isDownloadingXlsx ? <CircularProgress size={20} /> : 'Download xlsx'}
					</Button>
				</Grid>
				<Grid item>
					<Button variant='contained' size='small' fullWidth color='secondary' onClick={handleReset}>
						Reset
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};
